import { useEffect, useState, useRef, useCallback } from 'react';
import SearchComponent from '../searchComponent';
import { getUsers } from '../../api/system-account-api';
import { useSearchParams } from 'react-router-dom';
import { useConfig } from '../../providers/configProvider';
import { useLanguage } from '../../providers/preferenceProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faPlus, faCaretUp, faCaretDown } from '@fortawesome/free-solid-svg-icons';
import EditSystemAccount from '../overlays/editSystemAccount';
import { ACL } from '../../acls';

const SystemAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [count, setCount] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentSort = searchParams.get('sort') || 'firstname:ASC';
  const [sortKey, sortDirection] = currentSort.split(':');
  const searchTimeoutRef = useRef(null);
  const [hasMore, setHasMore] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [createNew, setCreateNew] = useState(false);

  const { isAuthorized } = useConfig();
  const pageSize = 10;
  const { t } = useLanguage();

  const fetchAccounts = useCallback(async (refresh = false) => {
    let localAccounts = refresh ? [] : [...accounts];
  
    const page = refresh ? 1 : Math.ceil(localAccounts.length / pageSize) + 1;
  
    const query = {
      search: searchParams.get('search') || '',
      sort: searchParams.get('sort') || 'name:ASC',
      pageSize,
      page,
    };
  
    const response = await getUsers(query, true);
  
    if (response?.data?.rows) {
      const newAccounts = refresh ? response.data.rows : [...localAccounts, ...response.data.rows];
      setAccounts(newAccounts);
      setCount(response.data.count);
      setHasMore(newAccounts.length < response.data.count);
    } else {
      setHasMore(false);
    }
  }, [searchParams, accounts]);
  

  useEffect(() => {
    fetchAccounts(true);
  }, []);

  const setSearch = (query) => {
    clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      searchParams.set('search', query);
      setSearchParams(searchParams);
      fetchAccounts(true);
    }, 300);
  };

  const setSort = (key) => {
    let newDirection = 'ASC';
    const current = searchParams.get('sort')?.split(':');
    if (current && current[0] === key) {
      newDirection = current[1] === 'ASC' ? 'DESC' : 'ASC';
    }
    
    searchParams.set('sort', `${key}:${newDirection}`);
    setSearchParams(searchParams);
    fetchAccounts(true);
  };

  const handleAddAccount = () => {
    setSelectedAccount(null);
    setCreateNew(true);
    setEditOpen(true);
  };
  
  const handleEdit = (account) => {
    setSelectedAccount(account);
    setCreateNew(false);
    setEditOpen(true);
  };
  const closeEditModal = () => setEditOpen(false);

  const handleUserUpdated = () => {
    fetchAccounts(true);
    closeEditModal();
  };

  return (
    <div className="admin-list-wrapper">
      <div className="flex md:flex-row flex-col gap-2 mb-2 justify-between"> 
        <SearchComponent onSearchChange={setSearch} />
        <button
          className="admin-add-button"
          disabled={!isAuthorized(ACL.USER)}
          onClick={handleAddAccount}
        >
          {t.add + " " + t.system_account} <FontAwesomeIcon icon={faPlus} />
        </button>
      </div>

      <div className="admin-list-header mb-4 flex justify-between items-center">
        <div>{t.amount_of + " " + t.system_accounts + ": " + count}</div>
        
      </div>

      <div className="admin-list-box">
        <div className="admin-list-header grid-cols-3 px-4 py-2">
          <div onClick={() => setSort('firstname')} className="cursor-pointer select-none">
            {t.name}
            {sortKey === 'firstname' && (<FontAwesomeIcon icon={sortDirection === 'ASC' ? faCaretUp : faCaretDown} className="table-sort-icon" />)}
          </div>
          <div onClick={() => setSort('email')} className="cursor-pointer select-none">
            {t.identifier}
            {sortKey === 'email' && (<FontAwesomeIcon icon={sortDirection === 'ASC' ? faCaretUp : faCaretDown} className="table-sort-icon" />)}
          </div>
        </div>

        {accounts.map((account, index) => (
          <div
            key={index}
            className="grid md:grid-cols-3 items-center px-3 py-2 rounded-md m-2 bg-[--white-color]"
          >
            <div className="font-medium">
              <span className="block md:hidden">{t.name + ": "}</span>
              {account.firstname}
            </div>
            <div className="text-sm">
              <span className="block md:hidden">{t.identifier + ": "}</span>
              {account.email}
            </div>
            <div className="text-right">
              <button
                className="admin-edit-button"
                disabled={!isAuthorized(ACL.USER)}
                onClick={() => handleEdit(account)}
              >
                {t.edit} <FontAwesomeIcon icon={faPenToSquare} />
              </button>
            </div>
          </div>
        ))}

        <div className="grid grid-cols-1 px-4 py-2">
          <div>
            <h2>{t.showing.replace("{ph_length}", accounts.length).replace("{ph_count}", count)}</h2>
          </div>
        </div>

        {hasMore && (
          <button className="admin-edit-button" onClick={() => fetchAccounts()}>
            {t.show_more}
          </button>
        )}
      </div>
      {editOpen && (
        <EditSystemAccount
          isOpen={editOpen}
          onClose={closeEditModal}
          systemAccount={selectedAccount}
          createNew={createNew}
          onSystemAccountUpdated={handleUserUpdated}
        />
      )}

    </div>
  );
};

export default SystemAccounts;
